import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { SaveTarget } from '../../lib/types'
import { sortLabel, takeBreakdown, takenSourceLine, type SidebarCard } from '../../lib/takes'
import { burrowAppUrl } from '../../lib/env'
import { thoughtStamp } from '../../lib/text'
import { btn } from '../ui'
import { PanelEmpty, PanelHeader, panelCard } from '../SidePanel'
import { SortMenu } from './SortMenu'
import '../../styles/reader.css'

// The reader's Taken sidebar: every line and quote they took from this
// writing, as cards like the composer's thoughts panel. Each is a loose
// thought until the reader sorts it — one card at a time, into one of their
// collections. Desktop shows it in a drawer over the page, opened from a
// "Taken · N" pill at the bottom right (TakenDock); phones open it as a sheet
// from a floating button (TakeBar.tsx).

/** How long the newest card glows after a take (ms). */
const FRESH_GLOW_MS = 1600

export interface TakenPanelProps {
  writingTitle: string
  author: string
  signedIn: boolean
  cards: readonly SidebarCard[]
  /** Who said each attributed pull quote, by block id: words from one are credited to them, via the writing. */
  saidBy?: ReadonlyMap<string, string>
  /** The reader's collections, most recent first. */
  collections: readonly SaveTarget[]
  /** Thought ids being moved right now. */
  moving: ReadonlySet<string>
  onSort(card: SidebarCard, collectionId: string | null): void
  /** Make a collection and sort the card into it; resolves false when it couldn't. */
  onCreate(card: SidebarCard, name: string): Promise<boolean>
  onRetry(card: SidebarCard): void
  onDismiss(card: SidebarCard): void
  onJoin(): void
  onClose(): void
  /** Bumps with each take: the newest card glows a moment, scrolled into view. */
  fresh?: number
}

export function TakenPanel({ writingTitle, author, signedIn, cards, saidBy, collections, moving, onSort, onCreate, onRetry, onDismiss, onJoin, onClose, fresh = 0 }: TakenPanelProps) {
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const names = new Map(collections.map((c) => [c.id, c.name]))
  const listRef = useRef<HTMLDivElement>(null)
  const [glow, setGlow] = useState(false)
  useEffect(() => {
    if (!fresh) return
    listRef.current?.scrollTo?.({ top: 0 })
    setGlow(true)
    const t = window.setTimeout(() => setGlow(false), FRESH_GLOW_MS)
    return () => window.clearTimeout(t)
  }, [fresh])

  return (
    <section aria-label="Taken" className="flex h-full min-h-0 flex-col font-sans">
      <div className="flex flex-col gap-1 px-[18px] pb-3 pt-[18px]">
        <PanelHeader closeLabel="Hide taken" onClose={onClose}>
          <h2 className="m-0 text-[13px] font-semibold text-foreground">
            Taken from <span className="font-display text-[14px] italic text-primary">{writingTitle || 'Untitled'}</span>
          </h2>
        </PanelHeader>
        <p className="m-0 text-[11.5px] font-medium text-muted-foreground">
          {signedIn ? 'Loose unless you sort them' : 'Kept in this browser until you join'}
        </p>
      </div>

      <div ref={listRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-5 pt-1">
        {cards.length === 0 ? (
          <PanelEmpty>
            Select any line to take it.
            <br />
            What you take collects here.
          </PanelEmpty>
        ) : (
          <ul aria-label="Taken cards" className="m-0 flex list-none flex-col gap-2 p-0">
            {cards.map((card, i) => (
              <TakenCardView
                key={card.key}
                card={card}
                fresh={glow && i === 0}
                source={takenSourceLine(writingTitle, author, saidBy?.get(card.blockId))}
                collections={collections}
                label={sortLabel(card.collectionIds, names)}
                moving={!!card.thoughtId && moving.has(card.thoughtId)}
                menuOpen={menuFor === card.key}
                onMenu={(open) => setMenuFor(open ? card.key : null)}
                onSort={(id) => onSort(card, id)}
                onCreate={(name) => onCreate(card, name)}
                onRetry={() => onRetry(card)}
                onDismiss={() => onDismiss(card)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-border/40 px-[18px] pb-4 pt-3.5">
        {signedIn ? (
          <a href={burrowAppUrl} className={`${btn.outline} w-full`}>
            Open Burrow
          </a>
        ) : (
          <>
            <span className="text-center text-[11.5px] font-medium text-muted-foreground">Keep what you take</span>
            <button type="button" onClick={onJoin} className={`${btn.primary} w-full`}>
              Take with you · Join Burrow
            </button>
          </>
        )}
      </div>
    </section>
  )
}

function TakenCardView({
  card,
  fresh,
  source,
  collections,
  label,
  moving,
  menuOpen,
  onMenu,
  onSort,
  onCreate,
  onRetry,
  onDismiss,
}: {
  card: SidebarCard
  /** Just taken: a moment's glow. */
  fresh: boolean
  source: string
  collections: readonly SaveTarget[]
  label: string
  moving: boolean
  menuOpen: boolean
  onMenu(open: boolean): void
  onSort(collectionId: string | null): void
  onCreate(name: string): Promise<boolean>
  onRetry(): void
  onDismiss(): void
}) {
  const quote = card.kind === 'quote'
  const sortable = card.status === 'saved' && !!card.thoughtId
  const tone = card.status === 'error' ? 'ring-1 ring-primary/30' : card.status === 'saved' ? '' : 'bg-card/70'
  const liRef = useRef<HTMLLIElement>(null)
  useEffect(() => {
    if (menuOpen) liRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [menuOpen])

  let status: ReactNode = null
  if (card.status === 'saving') {
    status = <span className="text-[11px] font-medium text-muted-foreground">Saving…</span>
  } else if (card.status === 'queued') {
    status = <span className="text-[11px] font-medium text-accent">Keeps when you join</span>
  } else if (card.status === 'error') {
    status = (
      <span role="alert" className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-medium text-primary">
        <span className="min-w-0">{card.error || 'Couldn’t take this.'}</span>
        <button type="button" onClick={onRetry} className="font-semibold underline-offset-2 hover:underline">
          Retry
        </button>
        <button type="button" onClick={onDismiss} className="text-muted-foreground hover:text-foreground">
          Dismiss
        </button>
      </span>
    )
  }

  return (
    <li
      ref={liRef}
      aria-busy={card.status === 'saving' || moving}
      data-fresh={fresh ? '' : undefined}
      className={`${panelCard} ${tone} ${fresh ? 'shadow-[0_0_0_2px_color-mix(in_srgb,var(--accent)_45%,transparent)]' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 text-[11px] font-medium text-muted-foreground">
        <span className="rounded-full bg-accent/10 px-2 py-px text-[10.5px] font-semibold tracking-[0.02em] text-accent">{quote ? 'Quote' : 'Line'}</span>
        {card.at && <time dateTime={card.at}>{thoughtStamp(card.at)}</time>}
      </div>
      <p className={`m-0 line-clamp-5 font-display text-[14px]/[1.6] text-foreground/85 ${quote ? 'italic' : ''}`}>{quote ? `“${card.text}”` : card.text}</p>
      <p title={source} className="m-0 truncate text-[11px] font-medium text-muted-foreground">
        {source}
      </p>
      {sortable ? (
        <SortMenu
          label={label}
          collectionIds={card.collectionIds}
          collections={collections}
          open={menuOpen}
          busy={moving}
          onOpen={onMenu}
          onSort={onSort}
          onCreate={onCreate}
          after={
            <a href={burrowAppUrl} className="shrink-0 text-[11px] font-medium text-primary hover:underline">
              Open in Burrow
            </a>
          }
        />
      ) : (
        status && <div className="flex min-h-[26px] items-center">{status}</div>
      )}
    </li>
  )
}

/** How long a peek stays out after a take before it tucks itself away (ms). */
export const TUCK_AFTER_MS = 2500
/** The tuck's slide out, before the drawer unmounts (ms). */
const TUCK_OUT_MS = 200

/** The Taken pill's tile: the Take button's blush dot. */
const TAKEN_DOT = <span aria-hidden className="size-2.5 shrink-0 rounded-full border-[1.5px] border-primary bg-accent/15" />

/**
 * On a desk (lib/layout.ts): a small "Taken · N" pill at the bottom right, in
 * view the whole way down the page, so a reader sees what they've gathered as
 * they scroll (before the first take it's a quiet "Taken", there to find).
 * It opens a 340px drawer over the page: an overlay rather than a reflow, as
 * the article's right margin holds notes and voices. `top` is where the
 * page's sticky header ends. The reading controls stack above the pill
 * (ReadingControls.tsx).
 *
 * "Peek, then tuck" (Niki, 10/6): a take opens the drawer as a peek (`peek`)
 * showing the new card, and it tucks itself away after TUCK_AFTER_MS — never
 * while the pointer is over it or focus is in it (sorting the card); the wait
 * starts over when they leave, and with each take (`peekSeq`). A press inside
 * keeps it (`onPin`); opened from the pill it stays until closed. A press
 * anywhere else, or Escape, closes it. A peek never takes focus; opening it
 * from the pill does, and closing it from inside hands focus back.
 *
 * In the author's preview the pill is shown inert.
 */
export function TakenDock({
  open,
  peek = false,
  peekSeq = 0,
  count,
  lines = 0,
  quotes = 0,
  top = 0,
  preview = false,
  onOpen,
  onClose,
  onPin,
  children,
}: {
  open: boolean
  /** Opened by a take, not the reader: it tucks itself away. */
  peek?: boolean
  /** Bumps with each take. */
  peekSeq?: number
  count: number
  lines?: number
  quotes?: number
  top?: number
  preview?: boolean
  onOpen?: () => void
  onClose?: () => void
  /** A press inside a peek: keep it open. */
  onPin?: () => void
  children?: ReactNode
}) {
  const drawer = open && !preview
  const asideRef = useRef<HTMLElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [tucking, setTucking] = useState(false)
  // Closed by a press elsewhere: focus stays where they pressed, not back on the pill.
  const closedOutside = useRef(false)
  // The latest props, for the listeners and timers below.
  const live = useRef({ onClose, onPin, peek })
  live.current = { onClose, onPin, peek }

  // The peek's wait: starts over with each take, and on leaving the drawer.
  useEffect(() => {
    setTucking(false)
    if (!drawer || !peek || hovered || focused) return
    const t = window.setTimeout(() => setTucking(true), TUCK_AFTER_MS)
    return () => window.clearTimeout(t)
  }, [drawer, peek, hovered, focused, peekSeq])
  useEffect(() => {
    if (!tucking) return
    const t = window.setTimeout(() => {
      setTucking(false)
      live.current.onClose?.()
    }, TUCK_OUT_MS)
    return () => window.clearTimeout(t)
  }, [tucking])

  // While open: a press elsewhere, or Escape, closes it; a press inside a peek keeps it.
  useEffect(() => {
    if (!drawer) return
    const onPointerDown = (e: PointerEvent) => {
      const at = e.target instanceof Node ? e.target : null
      if (at && asideRef.current?.contains(at)) {
        if (live.current.peek) live.current.onPin?.()
        return
      }
      if (at && pillRef.current?.contains(at)) return
      closedOutside.current = true
      live.current.onClose?.()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) live.current.onClose?.()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [drawer])

  // Opened from the pill: focus moves in. Closed from inside: back to the pill.
  const wasOpen = useRef(drawer)
  useEffect(() => {
    if (drawer && !wasOpen.current && !live.current.peek) asideRef.current?.focus()
    if (!drawer && wasOpen.current) {
      if (focused && !closedOutside.current) pillRef.current?.focus()
      closedOutside.current = false
      setHovered(false)
      setFocused(false)
    }
    wasOpen.current = drawer
  }, [drawer, focused])

  const what = takeBreakdown(lines, quotes)
  const some = count > 0
  let label = some ? `Show taken${what ? `: ${what}` : ''}` : 'Show taken: select any line to take it'
  if (preview) label = 'Taken: readers collect what they take here'
  return (
    <>
      <button
        ref={pillRef}
        type="button"
        data-taken-pill=""
        onClick={onOpen}
        disabled={preview}
        aria-expanded={preview ? undefined : drawer}
        aria-label={label}
        title={preview ? 'Readers collect the lines they take here' : some ? undefined : 'Select any line to take it'}
        className={`fixed bottom-5 right-4 z-30 mb-[env(safe-area-inset-bottom)] flex h-10 items-center gap-2 rounded-full border bg-card/95 pl-3.5 pr-4 font-sans text-[13px] font-semibold shadow-[var(--shadow-chip)] backdrop-blur-[8px] transition-colors disabled:cursor-default ${
          some ? 'border-accent/40 text-foreground hover:border-accent' : 'border-border/60 text-muted-foreground enabled:hover:text-foreground'
        }`}
      >
        {TAKEN_DOT}
        {/* Keyed by the count, so each take replays the bump. */}
        <span key={count} aria-hidden className={some ? 'wr-taken-bump' : undefined}>
          {some ? `Taken · ${count.toLocaleString()}` : 'Taken'}
        </span>
      </button>
      {drawer && (
        <aside
          ref={asideRef}
          tabIndex={-1}
          aria-label="Taken"
          data-taken-drawer={peek ? 'peek' : 'open'}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) setFocused(false)
          }}
          style={{ top }}
          className={`wr-taken-drawer ${tucking ? 'wr-taken-tuck' : ''} fixed bottom-0 right-0 z-30 w-[340px] border-l border-border/40 bg-sidebar shadow-[-18px_0_40px_rgba(45,27,30,.12)] outline-none`}
        >
          {children}
        </aside>
      )}
    </>
  )
}

